import { useCallback } from 'react';
import { useNoticeBannersStackContext } from '@sg/garnish';

import {
  useAddLineItemToCart,
  useNavigateToMenu,
  useNavigateToProductDetails,
} from '@order/hooks';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

import { useOpenBag } from '../../../components/Bag/Bag.hooks';
import { useProductForRestaurantQuery } from '../graphql/ProductForRestaurant.query.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const useLocationConfirm = (
  props: UseNavigateToBaseProductDetailsScreenProps,
) => {
  const {
    restaurantId = '',
    restaurantSlug,
    deliveryAddressId,
    baseProductSlug,
    deliveryVendorName,
    deliveryVendorRestaurantId,
    deliveryFee,
  } = props;

  const orderType = deliveryAddressId ? 'delivery' : 'pickup';

  const navigateToMenu = useNavigateToMenu();
  const openBag = useOpenBag({ shouldUpdateCart: true });
  const { track } = useTelemetry();
  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  // ─── Query ───────────────────────────────────────────────────────────

  const [productForRestaurantResponse] = useProductForRestaurantQuery({
    requestPolicy: 'cache-and-network',
    variables: { baseProductSlug, restaurantId },
    pause: !restaurantId,
  });

  // ─── Mutation ────────────────────────────────────────────────────────

  const onAddLineItemSuccess = useCallback(() => {
    navigateToMenu({
      orderType,
      restaurantSlug,
      deliveryOrderDetail: {
        addressId: deliveryAddressId,
      },
    });

    setTimeout(() => {
      openBag();
    }, 1000);
  }, [orderType, navigateToMenu, restaurantSlug, deliveryAddressId, openBag]);

  const { addLineItemToCart, fetching: isAddingLineItemToCart } =
    useAddLineItemToCart({
      onSuccess: onAddLineItemSuccess,
    });

  // ─── Flags ───────────────────────────────────────────────────────────

  const hasRestaurantData = Boolean(restaurantId);
  const productForRestaurantData =
    productForRestaurantResponse.data?.productForRestaurant;
  const productId = productForRestaurantData?.id;
  const productSlug = productForRestaurantData?.slug;

  const isFetchingProduct =
    productForRestaurantResponse.fetching || productForRestaurantResponse.stale;

  const isProductNotAvailable =
    hasRestaurantData && !isFetchingProduct && !productForRestaurantData;
  const isContinueButtonEnabled = useFeatureFlag(
    'MNU-656-deeplink-continue-button-enabled',
    {
      listenForChanges: true,
    },
  );

  // ─── Helpers ─────────────────────────────────────────────────────────

  const navigateToPDP = useNavigateToProductDetails({
    restaurantSlug,
    deliveryAddressId,
  });

  const handleLocationConfirm = useCallback(async () => {
    if (isProductNotAvailable) {
      navigateToMenu({
        orderType,
        restaurantSlug,
        deliveryOrderDetail: {
          addressId: deliveryAddressId,
        },
      });

      setTimeout(() => {
        addNoticeBanner({
          text: t('location.product-not-available'),
          palette: 'caution',
        });
      });

      return;
    }

    if (isContinueButtonEnabled) {
      track('deep-linking.continue_pdp_flow');

      navigateToPDP({ slug: productSlug });

      return;
    }

    track('deep-linking.order_now_flow');

    const isDeliveryOrder =
      orderType === 'delivery' &&
      deliveryAddressId !== undefined &&
      deliveryVendorName !== undefined &&
      deliveryVendorRestaurantId !== undefined &&
      deliveryFee !== undefined;

    const addLineItemToCartInputProps: Parameters<typeof addLineItemToCart>[0] =
      {
        product: { slug: productSlug, id: productId },
        deliveryDetails: isDeliveryOrder
          ? {
              tip: 0,
              deliveryFee,
              addressId: deliveryAddressId,
              vendor: deliveryVendorName,
              vendorRestaurantId: deliveryVendorRestaurantId,
            }
          : undefined,
      };

    await addLineItemToCart(addLineItemToCartInputProps);
  }, [
    addLineItemToCart,
    addNoticeBanner,
    deliveryAddressId,
    deliveryFee,
    deliveryVendorName,
    deliveryVendorRestaurantId,
    isContinueButtonEnabled,
    isProductNotAvailable,
    navigateToMenu,
    navigateToPDP,
    orderType,
    productId,
    productSlug,
    restaurantSlug,
    t,
    track,
  ]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    handleLocationConfirm,
    isProductNotAvailable,
    isFetchingProduct,
    isAddingLineItemToCart,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseNavigateToBaseProductDetailsScreenProps = Readonly<{
  baseProductSlug: string;
  restaurantId?: string;
  restaurantSlug?: string;
  deliveryAddressId?: string;
  deliveryVendorName?: string;
  deliveryVendorRestaurantId?: string;
  deliveryFee?: number;
}>;
